import { ChangeDetectionStrategy, Component, Optional, Self } from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { TranslocoPipe } from '@jsverse/transloco';
import { ICON_KEY_OPTIONS, IconKeyOption } from './icon-key-options';

@Component({
  selector: 'app-icon-key-select',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatFormFieldModule, MatIconModule, MatSelectModule, TranslocoPipe],
  templateUrl: './icon-key-select.component.html',
  styleUrl: './icon-key-select.component.scss',
})
export class IconKeySelectComponent implements ControlValueAccessor {
  readonly options = ICON_KEY_OPTIONS;

  value = '';
  disabled = false;

  private onChange: (value: string) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  constructor(@Optional() @Self() public readonly ngControl: NgControl | null) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  get selectedOption(): IconKeyOption {
    return this.findOption(this.value);
  }

  get selectableOptions(): readonly IconKeyOption[] {
    if (!this.value || this.options.some((option) => option.key === this.value)) {
      return this.options;
    }

    return [{ key: this.value }, ...this.options];
  }

  get showRequiredError(): boolean {
    const control = this.ngControl?.control;
    return !!control?.hasError('required') && (control.touched || control.dirty);
  }

  writeValue(value: string | null): void {
    this.value = value ?? '';
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onSelectionChange(value: string): void {
    this.value = value;
    this.onChange(value);
  }

  onClosed(): void {
    this.onTouched();
  }

  private findOption(value: string): IconKeyOption {
    return this.options.find((option) => option.key === value) ?? {
      key: value || this.options[0].key,
      labelKey: value ? undefined : this.options[0].labelKey,
    };
  }
}
